import React, { useEffect, useState } from 'react'
import { graphql, navigate } from 'gatsby'

import Seo from '../components/Seo/Seo'
import Layout from '../components/Layout/Layout'
import ListWithPagination from '../components/ListWithPagination/ListWithPagination'
import Hero, { HeroVariantEnum } from '../components/Hero/Hero'
import ListType from '../enums/ListType'
import FilterBar from '../components/FilterBar/FilterBar'
import styled from 'styled-components'
import NotFoundPage from './404'
import useListWithPagination from '../components/ListWithPagination/ListWithPagination.hook'
import { Helmet } from 'react-helmet'
import { paramsToObject } from '../utils/AddParamToUrl'
import RoutingEnum from '../routes/appRoutes'

const StyledError = styled.p`
  justify-content: center;
  display: flex;
`

const ERROR = 'Sorry, no results found. Try removing filters.'

const NewsPage = ({ data, location }) => {
  if (data?.contentfulPageListNews === null) {
    return <NotFoundPage location={location} />
  }

  const {
    backgroundColor,
    imageHeaderDesktop,
    imageHeaderMobile,
    pageTitle,
    seoMetaTitle,
    seoMetaDescription,
  } = data?.contentfulPageListNews

  const news = data?.allContentfulTemplateArticle.edges

  const isBrowser = typeof window !== `undefined`
  const hash = isBrowser ? location.hash.slice(1) : ''
  const urlParams = new URLSearchParams(hash)
  const { page, cat, years } = paramsToObject(urlParams.entries())

  const categoriesInit = cat ? cat.split('_') : []
  const yearsInit = years
    ? years.split('_').map((year) => parseInt(year, 10))
    : []

  const parametersInit = {
    page: page ? parseInt(page, 10) : 1,
    cat: categoriesInit,
    years: yearsInit,
  }

  const [parameters, setParameters] = useState(parametersInit)
  const [currentList, setCurrentList] = useState(news)
  const [count, setCount] = useState(1)

  useEffect(() => {
    const filteredList = applyFilters()
    const [orderedList] = useListWithPagination(filteredList)
    setCurrentList(orderedList[parameters.page - 1])
    setCount(orderedList.length)
    updateUrl()
  }, [parameters])

  const sortByCategoryHits = (list, filters) => {
    const filteredList = list.filter((el) =>
      el.node.categories.some((category) =>
        filters.some((el) => el === category.name)
      )
    )
    return filteredList.sort((prev, next) => {
      const numberOfMatches = (listCategories) => {
        return listCategories.filter((listCategory) =>
          filters.includes(listCategory.name)
        ).length
      }
      const prevMatchCount = numberOfMatches(prev.node.categories)
      const nextMatchCount = numberOfMatches(next.node.categories)
      return nextMatchCount - prevMatchCount
    })
  }

  const sortByYears = (list, filters) => {
    return list.filter((el) =>
      filters.some((year) => new Date(el.node.date).getFullYear() === year)
    )
  }

  const getParams = () => {
    const params = {}

    parameters.page > 1 && (params.page = parameters.page)
    parameters.cat.length && (params.cat = parameters.cat.join('_'))
    parameters.years.length && (params.years = parameters.years.join('_'))
    return params
  }

  const applyFilters = () => {
    let newList = news
    if (parameters.cat?.length >= 1) {
      newList = sortByCategoryHits(newList, parameters.cat)
    }
    if (parameters.years?.length >= 1) {
      newList = sortByYears(newList, parameters.years)
    }
    return newList
  }

  const onClickLink = (e, pageNumber, hash) => {
    e.preventDefault()
    setParameters({ ...parameters, page: pageNumber })
    navigate(`#${hash}`)
  }

  const updateUrl = () => {
    const params = getParams()
    const hash = new URLSearchParams(params).toString()
    hash ? navigate(`#${hash}`) : navigate(RoutingEnum.news)

    if (window) {
      // scroll top
      const scrollTop = document
        .querySelector('body')
        ?.getBoundingClientRect().top
      window?.scrollBy({
        top: scrollTop,
        left: 0,
        behavior: 'smooth',
      })
    }
  }

  const dontIndex =
    (parameters.cat.length > 0 || parameters.years.length > 0) ?? false

  return (
    <Layout>
      <Seo
        title={seoMetaTitle ? seoMetaTitle.seoMetaTitle : pageTitle}
        description={
          seoMetaDescription
            ? seoMetaDescription?.seoMetaDescription
            : pageTitle
        }
        canonicalHref={location.pathname}
      />
      {dontIndex && (
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
      )}
      <Hero
        backgroundColor={backgroundColor}
        mediaDesktop={imageHeaderDesktop}
        mediaMobile={imageHeaderMobile}
        pathname={location.pathname}
        title={pageTitle}
        variant={HeroVariantEnum.regular}
      />
      <FilterBar
        list={news}
        filteredCategory={parameters.cat}
        setParameters={setParameters}
        parameters={parameters}
        filteredYears={parameters.years}
        currentPage={parameters.page}
      />
      {currentList && currentList.length >= 1 ? (
        <ListWithPagination
          type={ListType.news}
          currentPage={parameters.page}
          currentList={currentList}
          count={count}
          onClickLink={onClickLink}
          parameters={parameters}
          setParameters={setParameters}
        />
      ) : (
        <StyledError>{ERROR}</StyledError>
      )}
    </Layout>
  )
}

export default NewsPage

export const news = graphql`
    query NewsPage {
        contentfulPageListNews {
            pageTitle
            backgroundColor
            imageHeaderDesktop {
                ...imageHeaderFragment
            }
            imageHeaderMobile {
                ...imageHeaderFragment
            }
            id
            seoMetaTitle {
                seoMetaTitle
            }
            seoMetaDescription {
                seoMetaDescription
            }
        }
        allContentfulTemplateArticle(
            sort: {date: DESC}
            filter: {node_locale: {eq: "en-US"}}
        ) {
            edges {
                node {
                    id
                    date
                    title
                    backgroundColor
                    imageHeaderDesktop {
                        ...imageHeaderFragment
                    }
                    imageHeaderMobile {
                        ...imageHeaderFragment
                    }
                    thumbnail {
                        name
                        altText {
                            altText
                        }
                        cloudinaryAsset {
                            resource_type
                            format
                            secure_url
                            context {
                                custom {
                                    alt
                                    caption
                                }
                            }
                        }
                        contentfulAsset {
                            file {
                                url
                            }
                            gatsbyImageData
                        }
                    }
                    slug
                    categories {
                        name
                    }
                    seoMetaDescription {
                        seoMetaDescription
                    }
                }
            }
        }
    }
`
